"use client";

import { useCallback, useMemo, useState } from "react";
import { HOME_VIEW, type MessengerTab, type MessengerView } from "./messenger-types";

export interface MessengerNav {
  readonly view: MessengerView;
  readonly canGoBack: boolean;
  readonly push: (view: MessengerView) => void;
  /** Swap the current view, e.g. the new chat once it has an id. */
  readonly replace: (view: MessengerView) => void;
  readonly back: () => void;
  readonly selectTab: (tab: MessengerTab) => void;
  readonly reset: (view: MessengerView) => void;
}

/** View stack of one messenger window; tabs start a fresh stack. */
export function useMessengerNav(initial: MessengerView = HOME_VIEW): MessengerNav {
  const [stack, setStack] = useState<readonly MessengerView[]>([initial]);

  const push = useCallback((view: MessengerView): void => {
    setStack((prev) => [...prev, view]);
  }, []);
  const replace = useCallback((view: MessengerView): void => {
    setStack((prev) => [...prev.slice(0, -1), view]);
  }, []);
  const back = useCallback((): void => {
    setStack((prev) => (prev.length > 1 ? prev.slice(0, -1) : prev));
  }, []);
  const selectTab = useCallback((tab: MessengerTab): void => {
    setStack([{ kind: tab }]);
  }, []);
  const reset = useCallback((view: MessengerView): void => {
    // Deep links keep a way back to the start view.
    setStack(view.kind === "home" ? [view] : [HOME_VIEW, view]);
  }, []);

  return useMemo(
    (): MessengerNav => ({
      view: stack[stack.length - 1] ?? HOME_VIEW,
      canGoBack: stack.length > 1,
      push,
      replace,
      back,
      selectTab,
      reset,
    }),
    [back, push, replace, reset, selectTab, stack],
  );
}
